import { useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  PaidMediaOverviewFunnelPlatform,
  PaidMediaOverviewGranularity,
  PaidMediaOverviewTrendPoint,
} from "src/services/PaidMediaOverviewAPI";
import {
  formatOverviewNumber,
  formatOverviewPct,
  formatOverviewText,
} from "../../utils/paidMediaOverviewFormat";
import CustomChartTooltip from "src/components/reusable/CustomChartTooltip/CustomChartTooltip";
import { overviewPlatformLabel } from "../../utils/paidMediaOverviewLabels";
import {
  OverviewChartEmptyState,
  OverviewChartLoadingState,
  OverviewMetricTip,
} from "./overviewPrimitives";
import {
  OVERVIEW_GOOGLE_COLOR,
  OVERVIEW_GRID_COLOR,
  OVERVIEW_META_COLOR,
  formatOverviewBucketLabel,
  overviewAxisProps,
} from "./overviewChartConfig";
import { usePaidMediaChartRtl } from "../../utils/paidMediaChartRtl";

const FUNNEL_STAGE_WIDTHS = ["full", "mid", "low"] as const;

interface ConversionRowProps {
  series: PaidMediaOverviewTrendPoint[];
  granularity: PaidMediaOverviewGranularity;
  funnelPlatforms: PaidMediaOverviewFunnelPlatform[];
  purchasesPeriodTotal: string | null;
  trendsLoading: boolean;
  funnelLoading: boolean;
}

const ConversionRow: FC<ConversionRowProps> = ({
  series,
  granularity,
  funnelPlatforms,
  purchasesPeriodTotal,
  trendsLoading,
  funnelLoading,
}) => {
  const { t, i18n } = useTranslation();
  const chartRtl = usePaidMediaChartRtl();

  const data = useMemo(
    () =>
      (series ?? []).map(point => ({
        date: formatOverviewBucketLabel(
          point?.date ?? "",
          granularity,
          date => t("paidMediaOverview.trend.weekOf", { date }),
          i18n.language
        ),
        metaPurchases: point?.meta_ads?.purchases ?? null,
        googlePurchases: point?.google_ads?.purchases ?? null,
      })),
    [series, granularity, t, i18n.language]
  );

  const renderPurchasesChart = () => {
    if (trendsLoading) {
      return <OverviewChartLoadingState />;
    }
    if (!data.length) {
      return <OverviewChartEmptyState />;
    }

    return (
      <ResponsiveContainer>
        <AreaChart data={data} margin={chartRtl.margin({ top: 5, right: 10, bottom: 5, left: -15 })}>
          <defs>
            <linearGradient id="paidMediaOverviewMetaFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={OVERVIEW_META_COLOR} stopOpacity={0.35} />
              <stop offset="100%" stopColor={OVERVIEW_META_COLOR} stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="paidMediaOverviewGoogleFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={OVERVIEW_GOOGLE_COLOR} stopOpacity={0.4} />
              <stop offset="100%" stopColor={OVERVIEW_GOOGLE_COLOR} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={OVERVIEW_GRID_COLOR} vertical={false} />
          <XAxis dataKey="date" reversed={chartRtl.axisReversed} {...overviewAxisProps} />
          <YAxis orientation={chartRtl.yAxisOrientation} {...overviewAxisProps} />
          <Tooltip
            wrapperClassName="paid-media-overview__chart-tooltip-wrapper"
            content={
              <CustomChartTooltip
                formatTooltip={(value: number) => formatOverviewNumber(value)}
              />
            }
          />
          <Area
            type="monotone"
            dataKey="metaPurchases"
            name={t("paidMediaOverview.conversion.seriesMetaPurchases")}
            stroke={OVERVIEW_META_COLOR}
            fill="url(#paidMediaOverviewMetaFill)"
            strokeWidth={2}
          />
          <Area
            type="monotone"
            dataKey="googlePurchases"
            name={t("paidMediaOverview.conversion.seriesGooglePurchases")}
            stroke={OVERVIEW_GOOGLE_COLOR}
            fill="url(#paidMediaOverviewGoogleFill)"
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  const renderFunnel = () => {
    if (funnelLoading) {
      return <OverviewChartLoadingState />;
    }
    if (!funnelPlatforms?.length) {
      return <OverviewChartEmptyState />;
    }

    return (
      <div className="paid-media-overview__funnel-grid">
        {funnelPlatforms.map(platform => (
          <Funnel key={platform?.platform} platform={platform} t={t} />
        ))}
      </div>
    );
  };

  return (
    <div className="paid-media-overview__two-col-grid">
      <article className="paid-media-panel">
        <div className="paid-media-overview__panel-header">
          <div>
            <h3>
              {t("paidMediaOverview.conversion.purchasesTitle")}
              <OverviewMetricTip
                id="paidPurchases"
                label={t("paidMediaOverview.conversion.purchasesTitle")}
              />
            </h3>
            <p className="paid-media-overview__panel-subtitle">
              {t("paidMediaOverview.conversion.purchasesSubtitle")}
            </p>
          </div>
          <span className="paid-media-overview__panel-header-note">
            {t("paidMediaOverview.conversion.periodTotal", {
              value: formatOverviewText(purchasesPeriodTotal),
            })}
          </span>
        </div>
        <div className="paid-media-overview__chart paid-media-overview__chart--md">
          {renderPurchasesChart()}
        </div>
      </article>

      <article className="paid-media-panel">
        <div className="paid-media-overview__panel-header">
          <div>
            <h3>
              {t("paidMediaOverview.conversion.funnelTitle")}
              <OverviewMetricTip
                id="clickToPurchase"
                label={t("paidMediaOverview.conversion.funnelTitle")}
              />
            </h3>
            <p className="paid-media-overview__panel-subtitle">
              {t("paidMediaOverview.conversion.funnelSubtitle")}
            </p>
          </div>
        </div>
        {renderFunnel()}
      </article>
    </div>
  );
};

const Funnel: FC<{
  platform: PaidMediaOverviewFunnelPlatform;
  t: TFunction;
}> = ({ platform, t }) => {
  const platformKey = platform?.platform === "meta_ads" ? "meta" : "google";

  const stages = [
    {
      key: "impressions",
      tooltipId: "impressions",
      label: t("paidMediaOverview.metrics.impressions"),
      value: platform?.impressions ?? null,
      formattedValue: platform?.formatted_impressions ?? null,
    },
    {
      key: "clicks",
      tooltipId: "clicks",
      label: t("paidMediaOverview.metrics.clicks"),
      value: platform?.clicks ?? null,
      formattedValue: platform?.formatted_clicks ?? null,
    },
    {
      key: "purchases",
      tooltipId: "paidPurchases",
      label: t("paidMediaOverview.metrics.purchases"),
      value: platform?.purchases ?? null,
      formattedValue: platform?.formatted_purchases ?? null,
    },
  ];

  return (
    <div className="paid-media-overview__funnel" data-platform={platformKey}>
      <div className="paid-media-overview__funnel-title">
        <span className="paid-media-overview__funnel-dot" aria-hidden />
        {overviewPlatformLabel(t, platform?.platform)}
      </div>
      <div className="paid-media-overview__funnel-stages">
        {stages.map((stage, index) => (
          <div key={stage.key}>
            <div className="paid-media-overview__funnel-stage-row">
              <span>
                {stage.label}
                <OverviewMetricTip id={stage.tooltipId} label={stage.label} />
              </span>
              <span className="paid-media-overview__funnel-stage-value">
                {stage.formattedValue ?? formatOverviewNumber(stage.value)}
              </span>
            </div>
            <div className="paid-media-overview__funnel-bar-progress-bar-center">
              <div
                className="paid-media-overview__funnel-bar"
                data-width={FUNNEL_STAGE_WIDTHS[index]}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="paid-media-overview__funnel-footer">
        <div>
          <div className="paid-media-overview__funnel-footer-label">
            {t("paidMediaOverview.metrics.ctr")}
            <OverviewMetricTip id="ctr" label={t("paidMediaOverview.metrics.ctr")} />
          </div>
          <div className="paid-media-overview__funnel-footer-value">
            {platform?.formatted_ctr ?? formatOverviewPct(platform?.ctr, 2)}
          </div>
        </div>
        <div>
          <div className="paid-media-overview__funnel-footer-label">
            {t("paidMediaOverview.conversion.clickToPurchase")}
            <OverviewMetricTip
              id="clickToPurchase"
              label={t("paidMediaOverview.conversion.clickToPurchaseLabel")}
            />
          </div>
          <div className="paid-media-overview__funnel-footer-value">
            {platform?.formatted_click_to_purchase_rate ??
              formatOverviewPct(platform?.click_to_purchase_rate, 2)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversionRow;
